import { UploadArea } from 'components/UploadArea';
import { css } from '@linaria/core';
import { UploadAreaProvider } from 'components/UploadArea/UploadAreaProvider';
import { useCallback, useEffect } from 'react';
import { useAsyncFn } from 'react-use';
import { blobToBase64 } from 'utils/general';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { Button } from 'components/Button';
import { useModalActions } from 'components/ModalManager/Context';
import { noop } from 'utils/fp';
import type { DropEvent, FileRejection } from 'react-dropzone';
import dynamic from 'next/dynamic';
import { extractMetadata, getUrlFileType } from 'utils/urls/files';
import { IModelPredictionStoreMST } from 'modules/Predictions/mst/ModelPredictionStore';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { IWorkflowPredictionStoreMST } from 'modules/Predictions/mst/WorkflowPredictionStore';
import { ConsentAcceptOverlay } from 'modules/Apps/components/ConsentAcceptOverlay';
import { isModelCheckConsents, isWorkflowCheckConsents } from 'utils/face';
import { CachedPostModelPredictBody } from 'modules/Predictions/effects';
import { downloadTextAsFile } from 'utils/downloadTextAsFile';
import { tiffToJpeg } from 'utils/inputs';
import { closeButton, jsonViewButtonContainer, jsonViewContainer, jsonViewScrollContainer } from './styles';
const ReactJson = dynamic(import('react-json-view'), {
  ssr: false
});
type Store = IModelPredictionStoreMST | IWorkflowPredictionStoreMST;
export function useUploadModal({
  title,
  store,
  mediaType,
  pageName,
  closeOnUnmount = false
}: {
  title: string;
  store: Store;
  mediaType: CF.UI.UISupportedUploadTypes;
  pageName: string;
  closeOnUnmount?: boolean;
}): {
  open: () => void;
  close: () => void;
} {
  const actions = useModalActions();
  // TODO: fix types
  const handleUpload = makeHandleUpload((store as IModelPredictionStoreMST));
  const {
    track
  } = useDataCollection();
  const closeUploadArea = useCallback(() => {
    actions.closeModal({
      id: 'predictionSidebar-uploadArea'
    });
  }, [actions]);
  const onPaste = usePasteCallback({
    store,
    mediaType,
    handleUpload,
    closeUploadArea
  });
  const onDrop = useDropCallback({
    mediaType,
    store,
    handleUpload,
    closeUploadArea
  });
  const openUploadArea = useCallback(() => {
    track('Upload Area Opened', {
      pageName
    });
    actions.openModal({
      title,
      content: <UploadAreaProvider onUrlPaste={onPaste} onDrop={onDrop} mediaType={mediaType} onTextSubmit={/* istanbul ignore next */raw => {
        const cacheKey = store.media.addContent({
          text: {
            raw
          }
        });
        handleUpload({
          text: {
            raw
          },
          cacheKey
        });
        actions.closeModal({
          id: 'predictionSidebar-uploadArea'
        });
      }}>
          <UploadArea />
          {isCheckConsents(store) && <ConsentAcceptOverlay onCancelClick={closeUploadArea} />}
          <Button className={closeButton} onClick={() => {
          actions.closeModal({
            id: 'predictionSidebar-uploadArea'
          });
        }}>
            Close
          </Button>
        </UploadAreaProvider>,
      id: 'predictionSidebar-uploadArea',
      // unique modal id
      makeActions: noop,
      // disable default "Close" modal action
      showBackdrop: true,
      closeOnBackdropClick: false,
      hideOnEsc: false
    });
  }, [actions, onPaste, onDrop, store.media]);

  // close modal on unmount if open
  useEffect(() => {
    return closeOnUnmount ? () => closeUploadArea() : () => null;
  }, [closeOnUnmount]);
  return {
    open: openUploadArea,
    close: closeUploadArea
  };
}
type ModelUploadHandler = (body: CF.API.ModelOutputs.PostModelPredictBody | CachedPostModelPredictBody) => void;
type WorkflowUploadHandler = (body: CF.API.ModelOutputs.PostModelPredictBody) => void;
function makeHandleUpload(store: IModelPredictionStoreMST | IWorkflowPredictionStoreMST): ModelUploadHandler | WorkflowUploadHandler {
  return store.entity.type === 'workflow' ? makeHandleUploadToWorkflow((store as IWorkflowPredictionStoreMST)) : makeHandleUploadToModel((store as IModelPredictionStoreMST));
}
function makeHandleUploadToModel(store: IModelPredictionStoreMST): ModelUploadHandler {
  return body => {
    store.predict(body);
  };
}
function makeHandleUploadToWorkflow(store: IWorkflowPredictionStoreMST): WorkflowUploadHandler {
  return body => {
    store.predict({
      inputs: [body]
    });
  };
}
function usePasteCallback({
  mediaType,
  handleUpload,
  closeUploadArea,
  store
}: {
  mediaType: CF.UI.UISupportedUploadTypes;
  handleUpload: ReturnType<typeof makeHandleUpload>;
  closeUploadArea: () => void;
  store: Store;
}): (url: string) => void {
  return useCallback( /* istanbul ignore next */
  (url: string) => {
    closeUploadArea();
    const fileType = getUrlFileType(url);
    if (mediaType === 'image') {
      handleUpload({
        image: {
          url
        }
      });
      store.media.addContent({
        image: {
          url
        }
      });
    } else if (mediaType === 'image,video') {
      if (fileType === 'image') {
        handleUpload({
          image: {
            url
          }
        });
        store.media.addContent({
          image: {
            url
          }
        });
      } else if (fileType === 'video') {
        handleUpload({
          video: {
            url
          }
        });
        store.media.addContent({
          video: {
            url
          }
        });
      }
    } else if (mediaType === 'text') {
      handleUpload({
        text: {
          url
        }
      });
      store.media.addContent({
        text: {
          url
        }
      });
    } else if (mediaType === 'audio') {
      handleUpload({
        audio: {
          url
        }
      });
      store.media.addContent({
        audio: {
          url
        }
      });
    }
  }, [store, closeUploadArea, handleUpload, mediaType]);
}
function useDropCallback({
  mediaType,
  store,
  handleUpload,
  closeUploadArea
}: {
  store: Store;
  mediaType: CF.UI.UISupportedUploadTypes;
  handleUpload: ReturnType<typeof makeHandleUpload>;
  closeUploadArea: () => void;
}): (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => Promise<void> {
  const [, onDrop] = useAsyncFn( /* istanbul ignore next */
  async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length && fileRejections.some(file => file.errors.some(err => err.code === 'file-too-large'))) {
      cogoToast.error('File too large. Please try a file under 25MB.');
    } else if (fileRejections.length && fileRejections.some(file => file.errors.some(err => err.code === 'too-many-files'))) {
      cogoToast.error('Only one file allowed at a time. Please try again.');
    } else if (acceptedFiles.length) {
      try {
        if (mediaType === 'text') {
          const text = await acceptedFiles[0].text();
          const textObj = {
            text: {
              raw: text
            }
          };
          handleUpload(textObj);
          store.media.addContent(textObj);
        } else if (mediaType === 'image') {
          const base64 = (await blobToBase64(acceptedFiles[0]) as string).split(';base64,')[1];
          const base64Obj = {
            image: {
              base64
            }
          };
          handleUpload(base64Obj);
          store.media.addContent(base64Obj);
        } else if (mediaType === 'audio') {
          const base64 = (await blobToBase64(acceptedFiles[0]) as string).split(';base64,')[1];
          const base64Obj = {
            audio: {
              base64
            }
          };
          handleUpload(base64Obj);
          store.media.addContent(base64Obj);
        } else if (mediaType === 'image,video') {
          const {
            type
          } = acceptedFiles[0];
          const isVideo = type.includes('video');
          const isTiffImage = type === 'image/tiff';
          const base64 = (await blobToBase64(acceptedFiles[0]) as string).split(';base64,')[1];
          if (isVideo) {
            const file = acceptedFiles[0];
            const {
              fps,
              framesCount
            } = await extractMetadata(file);
            // video upload
            const base64Obj = {
              video: {
                base64
              },
              metadata: {
                fps,
                framesCount
              }
            };
            handleUpload(base64Obj);
            store.media.addContent(base64Obj);
          } else if (isTiffImage) {
            // tiff images cannot be displayed directly in the browser so convert to jpeg
            const base64Jpej = await tiffToJpeg((base64 as string));
            const base64Obj = {
              image: {
                base64: base64Jpej
              }
            };
            handleUpload(base64Obj);
            store.media.addContent(base64Obj);
          } else {
            const base64Obj = {
              image: {
                base64
              }
            };
            handleUpload(base64Obj);
            store.media.addContent(base64Obj);
          }
        }
        closeUploadArea();
      } catch (e) {
        console.error(e);
        cogoToast.error('Invalid file, please try a different one.');
      }
    } else {
      cogoToast.error('Invalid file, please try a different one.');
    }
  }, [store, closeUploadArea, handleUpload, mediaType]);
  return onDrop;
}
export function useJSONModal(output: Record<string, unknown>, pageName: string): () => void {
  const actions = useModalActions();
  const {
    track
  } = useDataCollection();
  return () => {
    track('Use JSON Modal Opened', {
      pageName
    });
    actions.openModal({
      className: "odules__Predictions__components__PredictionSidebar__useUploadModal_tsx-className-cknwuhl",
      title: 'JSON Output',
      content: <div className={jsonViewContainer}>
          <div className={jsonViewScrollContainer}>
            <ReactJson src={output} theme="monokai" collapseStringsAfterLength={50} />
          </div>
          <div className={jsonViewButtonContainer}>
            <Button onClick={() => downloadTextAsFile(JSON.stringify(output, undefined, 2), `${pageName}_json_output`)}>Download</Button>
            <Button onClick={() => actions.closeModal({
            id: 'predictionSidebar-jsonViewer'
          })}>Close</Button>
          </div>
        </div>,
      id: 'predictionSidebar-jsonViewer',
      // unique modal id
      showBackdrop: true,
      closeOnBackdropClick: false,
      hideOnEsc: true,
      makeActions: noop // disable default "Close" modal action
    });
  };
}
export const useUploadModal_testable = {
  makeHandleUpload,
  usePasteCallback,
  useDropCallback
};
function isCheckConsents(store: Store): boolean {
  return store.entity.type === 'model' && isModelCheckConsents(store.entity.data) || store.entity.type === 'workflow' && isWorkflowCheckConsents(store.entity.data);
}

require("./useUploadModal.linaria.module.css!=!../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./useUploadModal.tsx");